// PlanCard: the staged workflow, laid out for approval.
//
// This is the thing the owner actually vets, so it renders the plan as the
// builder would draw it: same icons, same colours, same block vocabulary
// (BLOCK_STYLES), connected top to bottom. Approving here builds exactly this.
// The graph is already compiled server-side, and Build just commits it.

import { ArrowRight, Check, Loader2, PenLine, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BLOCK_STYLES } from "@/components/admin_dashboard/workflow_builder/blockStyles";
import type { CopilotPlan } from "@/lib/workflows/copilot";

interface PlanCardProps {
  plan: CopilotPlan;
  /** Live controls show only on the newest un-built plan. */
  interactive: boolean;
  committing: boolean;
  /** Set once built. The card locks and offers a way through to the builder. */
  committedWorkflowId?: string;
  onBuild: () => void;
  onRevise: () => void;
  onOpenWorkflow: (workflowId: string) => void;
}

export default function PlanCard({
  plan,
  interactive,
  committing,
  committedWorkflowId,
  onBuild,
  onRevise,
  onOpenWorkflow,
}: PlanCardProps) {
  const built = Boolean(committedWorkflowId);

  return (
    <div
      data-testid="copilot-plan-card"
      className="overflow-hidden rounded-xl border border-border bg-card shadow-sm"
    >
      {/* Header — what this workflow is */}
      <div className="border-b border-border bg-muted/30 px-4 py-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-foreground">{plan.name}</p>
            <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{plan.description}</p>
          </div>
          <span
            className={`shrink-0 rounded-md border px-2 py-1 text-[10px] font-medium uppercase tracking-wide ${
              built
                ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                : "border-border bg-background text-muted-foreground"
            }`}
          >
            {built ? "Built" : "Draft plan"}
          </span>
        </div>
      </div>

      {/* The steps, as a connected run */}
      <ol className="px-4 py-1">
        {plan.steps.map((step, index) => {
          const style = BLOCK_STYLES[step.kind] ?? BLOCK_STYLES.execution;
          const Icon = style.icon;
          const isLast = index === plan.steps.length - 1;

          return (
            <li key={`${step.kind}-${index}`} className="relative flex gap-3 pb-1 pt-3">
              {/* Rail */}
              <div className="flex flex-col items-center">
                <span
                  className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border ${style.chip}`}
                >
                  <Icon className="h-3.5 w-3.5" />
                </span>
                {!isLast && <span aria-hidden className="mt-1 w-px flex-1 bg-border" />}
              </div>

              {/* Body */}
              <div className={`min-w-0 flex-1 ${isLast ? "pb-3" : "pb-2"}`}>
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span className="text-[10px] font-semibold tabular-nums text-muted-foreground">
                    {index + 1}
                  </span>
                  <span className="text-sm font-medium leading-snug text-foreground">{step.label}</span>
                  {step.requiresApproval && (
                    <span className="inline-flex items-center gap-1 rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
                      <ShieldCheck className="h-2.5 w-2.5" />
                      Needs your approval
                    </span>
                  )}
                </div>

                {step.detail && (
                  <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{step.detail}</p>
                )}

                {step.params && step.params.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {step.params.map((param) => (
                      <span
                        key={param}
                        className="rounded border border-border bg-muted/50 px-1.5 py-0.5 text-[10px] text-muted-foreground"
                      >
                        {param}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ol>

      {/* Actions */}
      <div className="border-t border-border bg-muted/20 px-4 py-3">
        {built ? (
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="flex items-center gap-1.5 text-xs text-emerald-700">
              <Check className="h-3.5 w-3.5" />
              Saved as a draft. Nothing sends until you publish it.
            </p>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="gap-1.5"
              onClick={() => committedWorkflowId && onOpenWorkflow(committedWorkflowId)}
            >
              Open in builder
              <ArrowRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        ) : interactive ? (
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              size="sm"
              className="gap-1.5"
              disabled={committing}
              onClick={onBuild}
              data-testid="copilot-build-plan"
            >
              {committing ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Building…
                </>
              ) : (
                <>
                  <Check className="h-3.5 w-3.5" />
                  Build this workflow
                </>
              )}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="gap-1.5 text-muted-foreground"
              disabled={committing}
              onClick={onRevise}
            >
              <PenLine className="h-3.5 w-3.5" />
              Change something
            </Button>
            <p className="ml-auto text-[11px] text-muted-foreground">
              {plan.nodeCount} of 30 blocks
            </p>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">
            Superseded — scroll down for the current plan.
          </p>
        )}
      </div>
    </div>
  );
}
